import { useState, useCallback, useRef, useEffect } from 'react'
import {
  transcribeService,
  SpecialtyType,
} from 'lib/services/transcribe/transcribeService'
import { TranscribeStreamingClient } from '@aws-sdk/client-transcribe-streaming'
import { TemporaryCredentials } from 'lib/services/restApi/restApi.types'

interface UseTranscriptionOptions {
  specialty?: SpecialtyType
  temporaryCredentials?: TemporaryCredentials
}

interface UseTranscriptionReturn {
  transcript: string
  isRecording: boolean
  isProcessing: boolean
  error: string | null
  startRecording: () => Promise<void>
  stopRecording: () => void
  reset: () => void
}

const convertFloat32ToPCM16 = (input: Float32Array): Uint8Array => {
  const output = new Uint8Array(input.length * 2)
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    const int16 = s < 0 ? s * 0x8000 : s * 0x7fff
    output[i * 2] = int16 & 0xff
    output[i * 2 + 1] = (int16 >> 8) & 0xff
  }
  return output
}

const removeFillerWords = (text: string): string => {
  const fillerWords = [
    /\b(uh|um|er|ah|like|you know|sort of|kind of)\b/gi,
    /\b(so|well|basically|actually|literally|honestly)\b/gi,
  ]

  return fillerWords
    .reduce((cleanedText, pattern) => {
      return cleanedText.replace(pattern, '')
    }, text)
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
}

export const useTranscription = (
  options: UseTranscriptionOptions = {}
): UseTranscriptionReturn => {
  const [transcript, setTranscript] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Refs for audio processing
  const streamRef = useRef<MediaStream | null>(null)
  const clientRef = useRef<TranscribeStreamingClient | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const transcriptStreamRef = useRef<any>(null)
  const lastProcessedTranscriptRef = useRef('')

  const reset = useCallback(() => {
    setTranscript('')
    lastProcessedTranscriptRef.current = ''
    setError(null)
    setIsProcessing(false)
  }, [])

  const cleanup = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect()
      processorRef.current.onaudioprocess = null
      processorRef.current = null
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect()
      analyserRef.current = null
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error)
      audioContextRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    if (clientRef.current) {
      clientRef.current.destroy()
      clientRef.current = null
    }

    transcriptStreamRef.current = null
  }, [])

  const processTranscriptStream = useCallback(async (stream: any) => {
    try {
      for await (const event of stream) {
        if (
          event.TranscriptEvent?.Transcript?.Results &&
          event.TranscriptEvent.Transcript.Results.length > 0
        ) {
          const result = event.TranscriptEvent.Transcript.Results[0]
          if (result.IsPartial === false) {
            const newTranscript = result.Alternatives?.[0]?.Transcript || ''
            if (
              newTranscript &&
              newTranscript !== lastProcessedTranscriptRef.current
            ) {
              const cleanedTranscript = removeFillerWords(newTranscript)
              setTranscript(cleanedTranscript)
              lastProcessedTranscriptRef.current = newTranscript
            }
          }
        }
      }
    } catch (error) {
      console.error('Error processing transcript stream:', error)
      setError('Error processing transcript stream. Please try again.')
    }
  }, [])

  const startRecording = useCallback(async () => {
    try {
      setIsProcessing(true)
      setError(null)
      reset()

      if (!options.temporaryCredentials) {
        throw new Error('AWS credentials not provided')
      }

      // Initialize transcribe service with credentials
      transcribeService.initialize({
        region: options.temporaryCredentials.region,
        credentials: {
          accessKeyId: options.temporaryCredentials.credentials.AccessKeyId,
          secretAccessKey:
            options.temporaryCredentials.credentials.SecretAccessKey,
          sessionToken: options.temporaryCredentials.credentials.SessionToken,
        },
      })

      // Get microphone permission
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      // Set up AudioContext
      audioContextRef.current = new AudioContext({
        sampleRate: 16000,
      })

      const source = audioContextRef.current.createMediaStreamSource(
        streamRef.current
      )
      analyserRef.current = audioContextRef.current.createAnalyser()
      processorRef.current = audioContextRef.current.createScriptProcessor(
        4096,
        1,
        1
      )

      source.connect(analyserRef.current)
      analyserRef.current.connect(processorRef.current)
      processorRef.current.connect(audioContextRef.current.destination)

      // Create audio stream
      const audioStream = async function* () {
        const queue: Uint8Array[] = []
        let resolve:
          | ((
              value: IteratorResult<{ AudioEvent: { AudioChunk: Uint8Array } }>
            ) => void)
          | null = null
        let receivedFinalChunk = false

        processorRef.current!.onaudioprocess = (event) => {
          const inputData = event.inputBuffer.getChannelData(0)
          const pcmData = convertFloat32ToPCM16(inputData)

          if (resolve) {
            resolve({
              value: { AudioEvent: { AudioChunk: pcmData } },
              done: false,
            })
            resolve = null
          } else {
            queue.push(pcmData)
          }
        }

        const stopProcessing = () => {
          if (processorRef.current) {
            processorRef.current.disconnect()
            processorRef.current.onaudioprocess = null
          }
          receivedFinalChunk = true
          if (resolve) {
            resolve({ value: undefined, done: true })
          }
        }

        if (streamRef.current) {
          streamRef.current.getAudioTracks()[0].onended = stopProcessing
        }

        while (true) {
          if (queue.length > 0) {
            const chunk = queue.shift()!
            yield { AudioEvent: { AudioChunk: chunk } }
          } else if (receivedFinalChunk) {
            break
          } else {
            try {
              await new Promise<
                IteratorResult<{ AudioEvent: { AudioChunk: Uint8Array } }>
              >((r) => {
                resolve = r
              }).then((result) => {
                if (!result.done) {
                  queue.push(result.value.AudioEvent.AudioChunk)
                }
              })
            } catch (error) {
              throw error
            }
          }
        }
      }

      // Start transcription
      const transcribeStream =
        await transcribeService.startMedicalTranscription(
          audioStream(),
          options.specialty
        )

      const transcriptStream = transcribeStream?.TranscriptResultStream
      if (transcriptStream) {
        transcriptStreamRef.current = transcriptStream
        processTranscriptStream(transcriptStream)
      } else {
        throw new Error('No transcript stream received')
      }

      setIsRecording(true)
      setIsProcessing(false)
    } catch (error) {
      console.error('Error starting transcription:', error)
      setIsProcessing(false)
      setError(
        error instanceof Error ? error.message : 'Failed to start transcription'
      )
      cleanup()
    }
  }, [
    options.specialty,
    options.temporaryCredentials,
    cleanup,
    processTranscriptStream,
    reset,
  ])

  const stopRecording = useCallback(() => {
    cleanup()
    setIsRecording(false)
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  return {
    transcript,
    isRecording,
    isProcessing,
    error,
    startRecording,
    stopRecording,
    reset,
  }
}
